import { useEffect, useRef } from "react";
import orbTextureSrc from "./assets/orb-texture.png";
import { MAX_DPR, SIZE_PX } from "./constants";
import { createOrbRenderer, type OrbLayers, type OrbRenderer } from "./renderer";
import type { WakeOrbSize, WakeOrbState } from "./types";

/**
 * One shared decode per asset for every orb on the page — 176x176, so there is
 * no reason for each instance to fetch it.
 */
const cache = new Map<string, Promise<HTMLImageElement>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  let pending = cache.get(src);
  if (!pending) {
    pending = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`WakeOrb: could not load ${src}`));
      img.src = src;
    });
    cache.set(src, pending);
  }
  return pending;
}

const LAYER_SOURCES: Record<keyof OrbLayers, string> = {
  texture: orbTextureSrc,
};

type Options = {
  state: WakeOrbState;
  size: WakeOrbSize;
  level: number;
};

/**
 * Owns the canvas lifecycle: DPR-correct sizing, the animation loop, and the
 * rules for when not to run it. Props are pushed into the renderer through
 * setters so none of this re-mounts when `state` or `level` changes.
 */
export function useOrbCanvas({ state, size, level }: Options) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<OrbRenderer | null>(null);
  const rafRef = useRef(0);
  const prevStateRef = useRef<WakeOrbState | null>(null);
  const onScreenRef = useRef(true);
  const syncRef = useRef<() => void>(() => {});
  const measureRef = useRef<() => void>(() => {});

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;

    const renderer = createOrbRenderer(canvas);
    rendererRef.current = renderer;
    let disposed = false;

    const measure = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      // The host box is the *designed* orb size; the canvas overhangs it (see
      // CANVAS_BLEED) so the glow has somewhere to go.
      const orbCssSize = host.getBoundingClientRect().width || SIZE_PX.medium;
      renderer.resize(orbCssSize, dpr);
    };
    measureRef.current = measure;
    measure();

    const tick = (now: number) => {
      if (disposed) return;
      if (renderer.frame(now)) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        // Settled on a still frame — stop burning rAF until something changes.
        rafRef.current = 0;
      }
    };

    const sync = () => {
      if (disposed) return;
      const shouldRun = onScreenRef.current && !document.hidden;
      if (shouldRun) {
        if (rafRef.current === 0) rafRef.current = requestAnimationFrame(tick);
      } else if (rafRef.current !== 0) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
    syncRef.current = sync;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    renderer.setReducedMotion(motionQuery.matches);

    for (const [layer, src] of Object.entries(LAYER_SOURCES)) {
      loadImage(src)
        .then((img) => {
          if (disposed) return;
          renderer.setLayer(layer as keyof OrbLayers, img);
          sync();
        })
        .catch(() => {
          // The disc, ring and mic still render without their bitmap layers.
        });
    }

    const resizeObserver = new ResizeObserver(() => {
      measure();
      sync();
    });
    resizeObserver.observe(host);

    const intersectionObserver = new IntersectionObserver((entries) => {
      onScreenRef.current = entries.some((entry) => entry.isIntersecting);
      sync();
    });
    intersectionObserver.observe(host);

    const onVisibility = () => sync();
    // Catches a DPR change from dragging the window to another monitor.
    const onWindowResize = () => {
      measure();
      sync();
    };
    const onMotionChange = (event: MediaQueryListEvent) => {
      renderer.setReducedMotion(event.matches);
      sync();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("resize", onWindowResize);
    motionQuery.addEventListener("change", onMotionChange);

    sync();

    return () => {
      disposed = true;
      if (rafRef.current !== 0) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onWindowResize);
      motionQuery.removeEventListener("change", onMotionChange);
      renderer.dispose();
      rendererRef.current = null;
    };
  }, []);

  useEffect(() => {
    const from = prevStateRef.current;
    if (from !== state) {
      console.log("[wake-orb] state", from === null ? "mount" : from, "→", state);
      prevStateRef.current = state;
    }
    rendererRef.current?.setState(state);
    // A state change always needs frames again, even from a settled stop.
    syncRef.current();
  }, [state]);

  useEffect(() => {
    // No sync() here: states that react to level animate continuously anyway,
    // and states that ignore it must not be woken by a live mic.
    rendererRef.current?.setLevel(level);
  }, [level]);

  useEffect(() => {
    measureRef.current();
    syncRef.current();
  }, [size]);

  return { hostRef, canvasRef };
}
